import { useAtom, useAtomValue } from "jotai";
import { ortexColorState, textColorState, userState } from "@/lib/store";
import { Settings } from "@/models/auth";
import { Box, Button, Collapse, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, IconButton, TextField, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { FC, useEffect, useState } from "react";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import InfoIcon from "@mui/icons-material/Info";
import { isEqual } from "lodash";
import SnackBar from "../snack-bar/SnackBar";
import { updateUserSettings } from "@/controllers/auth";

type NamedFilterSet = {
    id: string;
    name: string;
    customFilterModel: { items: any[] };
};

type SaveFiltersButtonProps = {
    tableId: string;
    values: any[];
    columnConfiguration?: any;
    onColumnConfigurationSaved?: (columns: any[]) => void;
    onColumnConfigurationDeleted?: () => void;
    resetOptions?: { setResetFilters: any; setChips: (chips: Array<any>) => void; reset: { setter; defaultValue }; DataGrid: boolean; setParams: (params: any) => void; fetcher; defaultValues };
    maxSets?: number;
};

const SaveFiltersButton: FC<SaveFiltersButtonProps> = ({ tableId, values, columnConfiguration, onColumnConfigurationSaved, onColumnConfigurationDeleted, resetOptions, maxSets }) => {
    const textColor = useAtomValue(textColorState);
    const ortexColor = useAtomValue(ortexColorState);
    const [user, setUser] = useAtom(userState);
    const [open, setOpen] = useState<Boolean>(false);
    const [dialogType, setDialogType] = useState<"save" | "delete">();
    const [hasSavedFilters, setHasSavedFilters] = useState<Boolean>();

    const [presetsOpen, setPresetsOpen] = useState(true);
    const [overwriteDialogOpen, setOverwriteDialogOpen] = useState(false);
    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [renameId, setRenameId] = useState<string | null>(null);
    const [renameName, setRenameName] = useState("");

    const [snackBar, setSnackBar] = useState<{ open: boolean; text: string; severity: "success" | "error" | "info" | "warning" }>({
        open: false,
        text: "",
        severity: "success",
    });

    const is4k = useMediaQuery("(min-width: 2560px)");

    const savedTableSettings = user.settings.module_settings?.["datagrid->saved-filters"]?.[tableId] || {};
    const filterSets: NamedFilterSet[] = savedTableSettings.filterSets || [];
    const defaultPresetId: string | undefined = savedTableSettings.defaultPreset;

    // Auto-detect which saved set matches the current active filters (if any)
    const activeSetIndex = filterSets.findIndex((set) => isEqual(set.customFilterModel?.items ?? [], values ?? []));

    useEffect(() => {
        user.settings.module_settings?.["datagrid->saved-filters"] && setHasSavedFilters(user.settings.module_settings?.["datagrid->saved-filters"].hasOwnProperty(tableId));
    }, [user.settings.module_settings?.["datagrid->saved-filters"]]);

    // One-time migration: convert legacy top-level customFilterModel to a named filterSet
    useEffect(() => {
        if (!maxSets) return;
        const tableSettings = user.settings.module_settings?.["datagrid->saved-filters"]?.[tableId];
        if (!tableSettings?.customFilterModel || tableSettings?.filterSets) return;

        const { customFilterModel, ...rest } = tableSettings;
        const newSavedDGFilters = {
            ...user.settings.module_settings?.["datagrid->saved-filters"],
            [tableId]: { ...rest, filterSets: [{ id: Date.now().toString(), name: "My saved filters", customFilterModel }] },
        };
        const settings: Settings = { ...user.settings, module_settings: { ...user.settings?.module_settings, ["datagrid->saved-filters"]: newSavedDGFilters } };
        setUser({ ...user, settings });
        if (process.env.NEXT_PUBLIC_VERCEL_ENV) {
            try {
                updateUserSettings(settings);
            } catch (error) {
                console.error("error migrating saved filters", error);
            }
        }
        setSnackBar({ open: true, text: "Your saved filters were imported as a preset", severity: "info" });
    }, []);

    const { setResetFilters, setChips, reset, DataGrid, setParams, fetcher, defaultValues } = resetOptions || {};

    const resetTable = () => {
        try {
            setResetFilters && setResetFilters(true);
            setChips && setChips([]);

            if (reset?.defaultValue && reset?.setter) {
                const { setter, defaultValue } = reset;
                if (Array.isArray(setter)) setter.forEach((set, i) => set(defaultValue[i]));
                else setter(defaultValue);
            }

            if (DataGrid) {
                setParams(defaultValues.apiParams);
                fetcher(1, undefined, defaultValues);
            }
        } catch (error) {
            console.error("error reseting table", error);
        }
    };

    const persistSettings = (newSavedDGFilters: any, successText: string) => {
        const settings: Settings = { ...user.settings, module_settings: { ...user.settings?.module_settings, ["datagrid->saved-filters"]: newSavedDGFilters } };
        setUser({ ...user, settings });

        if (process.env.NEXT_PUBLIC_VERCEL_ENV) {
            try {
                updateUserSettings(settings);
                setSnackBar({ open: true, text: successText, severity: "success" });
            } catch (error) {
                console.error("error updating user settings", error);
                setSnackBar({ open: true, text: "Error saving filters", severity: "error" });
            }
        }
    };

    const saveNamedSet = (name: string, overwriteIdx: number | null) => {
        const newSavedDGFilters = { ...user.settings.module_settings?.["datagrid->saved-filters"] };
        const currentTableSettings = { ...(newSavedDGFilters[tableId] || {}) };
        const currentSets = [...(currentTableSettings.filterSets || [])];

        const newSet: NamedFilterSet = {
            id: overwriteIdx !== null ? (currentSets[overwriteIdx]?.id ?? Date.now().toString()) : Date.now().toString(),
            name,
            customFilterModel: { items: values },
        };

        if (overwriteIdx !== null) {
            currentSets[overwriteIdx] = newSet;
        } else {
            currentSets.push(newSet);
        }

        newSavedDGFilters[tableId] = { ...currentTableSettings, filterSets: currentSets };
        persistSettings(newSavedDGFilters, `"${name}" saved`);
    };

    const handleQuickSave = () => {
        if (filterSets.length >= maxSets) {
            setOverwriteDialogOpen(true);
        } else {
            saveNamedSet(`Filter ${filterSets.length + 1}`, null);
        }
    };

    const handleOverwrite = (index: number) => {
        saveNamedSet(filterSets[index]?.name ?? `Filter ${index + 1}`, index);
        setOverwriteDialogOpen(false);
    };

    const handleRename = () => {
        if (!renameId || !renameName.trim()) return;
        const newSavedDGFilters = { ...user.settings.module_settings?.["datagrid->saved-filters"] };
        const currentTableSettings = { ...(newSavedDGFilters[tableId] || {}) };
        const currentSets = (currentTableSettings.filterSets ?? []).map((s: NamedFilterSet) => (s.id === renameId ? { ...s, name: renameName.trim() } : s));
        newSavedDGFilters[tableId] = { ...currentTableSettings, filterSets: currentSets };
        persistSettings(newSavedDGFilters, `Renamed to "${renameName.trim()}"`);
        setRenameId(null);
        setRenameName("");
    };

    const confirmDeleteNamedSet = () => {
        if (!deleteId) return;
        const newSavedDGFilters = { ...user.settings.module_settings?.["datagrid->saved-filters"] };
        const currentTableSettings = { ...(newSavedDGFilters[tableId] || {}) };
        const currentSets = [...(currentTableSettings.filterSets || [])];
        const deletedName = currentSets.find((s) => s.id === deleteId)?.name;
        newSavedDGFilters[tableId] = { ...currentTableSettings, filterSets: currentSets.filter((s) => s.id !== deleteId) };
        persistSettings(newSavedDGFilters, `"${deletedName}" deleted`);
        setDeleteId(null);
    };

    const handleLoadNamedSet = (filterSet: NamedFilterSet) => {
        setParams && setParams({ customFilterModel: filterSet.customFilterModel });
    };

    const handleToggleDefault = (index: number) => {
        const newSavedDGFilters = { ...user.settings.module_settings?.["datagrid->saved-filters"] };
        const currentTableSettings = { ...(newSavedDGFilters[tableId] || {}) };
        const presetId = filterSets[index]?.id;
        const isAlreadyDefault = currentTableSettings.defaultPreset === presetId;
        newSavedDGFilters[tableId] = {
            ...currentTableSettings,
            defaultPreset: isAlreadyDefault ? undefined : presetId,
        };
        persistSettings(newSavedDGFilters, isAlreadyDefault ? "Default preset removed" : `"${filterSets[index]?.name}" set as default`);
    };

    // Multi-set UI
    if (maxSets) {
        const hasActive = activeSetIndex !== -1;

        return (
            <Box sx={{ width: "100%", mb: is4k ? 3 : 1, mt: 1 }}>
                <Divider sx={{ mb: 0.5 }} />

                {/* Collapsible header row */}
                <Box
                    onClick={() => setPresetsOpen((prev) => !prev)}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer",
                        py: 0.5,
                        px: 0.5,
                        borderRadius: 1,
                        "&:hover": { backgroundColor: "action.hover" },
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="overline" sx={{ color: textColor, fontWeight: 600, letterSpacing: 1, lineHeight: 1 }}>
                            Filter Presets
                        </Typography>
                        {hasActive && <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: ortexColor, flexShrink: 0 }} />}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Tooltip
                            arrow
                            title={
                                <>
                                    <Typography variant="body2">Save up to {maxSets} named filter presets.</Typography>
                                    <Box component="ul" sx={{ mt: 0.5, pl: 2, mb: 0 }}>
                                        <li>
                                            <Typography variant="body2">Click a preset name to apply it</Typography>
                                        </li>
                                        <li>
                                            <Typography variant="body2" sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                                                <BookmarkIcon sx={{ fontSize: 13, flexShrink: 0, mt: "4px" }} /> marks which preset loads by default on page open
                                            </Typography>
                                        </li>
                                        <li>
                                            <Typography variant="body2">× removes a preset</Typography>
                                        </li>
                                    </Box>
                                </>
                            }
                        >
                            <InfoIcon fontSize="small" sx={{ fontSize: 14, color: textColor, opacity: 0.5 }} onClick={(e) => e.stopPropagation()} />
                        </Tooltip>
                        {presetsOpen ? <ExpandLessIcon sx={{ fontSize: 16, color: textColor }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: textColor }} />}
                    </Box>
                </Box>

                <Collapse in={presetsOpen} timeout={150}>
                    <Box sx={{ display: "flex", flexDirection: "column", pt: 0.5 }}>
                        {Array.from({ length: maxSets }).map((_, index) => {
                            const set = filterSets[index];
                            const isActive = activeSetIndex === index;

                            if (!set) {
                                return (
                                    <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 30, px: 0.5, borderRadius: 1, opacity: 0.3 }}>
                                        <Box sx={{ width: 7, height: 7, borderRadius: "50%", border: "1.5px solid", borderColor: textColor, flexShrink: 0 }} />
                                        <Typography variant="body2" sx={{ flex: 1, fontStyle: "italic", fontSize: "0.78rem" }}>
                                            empty
                                        </Typography>
                                    </Box>
                                );
                            }

                            const isDefault = filterSets.length === 1 || (!!set.id && defaultPresetId === set.id);

                            return (
                                <Tooltip key={index} title="Apply this preset" placement="left" enterDelay={600}>
                                    <Box
                                        onClick={() => handleLoadNamedSet(set)}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            minHeight: 30,
                                            px: 0.5,
                                            borderRadius: 1,
                                            cursor: "pointer",
                                            "&:hover": { backgroundColor: "action.hover" },
                                            "&:hover .rename-btn": { opacity: 1 },
                                            "&:hover .delete-btn": { opacity: 1 },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 7,
                                                height: 7,
                                                borderRadius: "50%",
                                                flexShrink: 0,
                                                backgroundColor: isActive ? ortexColor : "transparent",
                                                border: "1.5px solid",
                                                borderColor: isActive ? ortexColor : textColor,
                                                transition: "all 0.15s",
                                            }}
                                        />
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                flex: 1,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                fontSize: "0.82rem",
                                                fontWeight: isActive ? 600 : 400,
                                                color: isActive ? ortexColor : textColor,
                                            }}
                                        >
                                            {set.name}
                                        </Typography>
                                        <Tooltip title="Rename" placement="top">
                                            <IconButton
                                                className="rename-btn"
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setRenameId(set.id);
                                                    setRenameName(set.name);
                                                }}
                                                sx={{ opacity: 0, transition: "opacity 0.15s", p: "2px" }}
                                            >
                                                <EditOutlinedIcon sx={{ fontSize: 13, color: textColor }} />
                                            </IconButton>
                                        </Tooltip>
                                        {filterSets.length > 1 && (
                                            <Tooltip title={isDefault ? "Remove as default" : "Set as default"} placement="top">
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleToggleDefault(index);
                                                    }}
                                                    sx={{ p: "2px" }}
                                                >
                                                    {isDefault ? <BookmarkIcon sx={{ fontSize: 13, color: ortexColor }} /> : <BookmarkBorderIcon sx={{ fontSize: 13, color: textColor, opacity: 0.4 }} />}
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <IconButton
                                            className="delete-btn"
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setDeleteId(set.id);
                                            }}
                                            sx={{ opacity: 0, transition: "opacity 0.15s", p: "2px" }}
                                        >
                                            <CloseIcon sx={{ fontSize: 13, color: textColor }} />
                                        </IconButton>
                                    </Box>
                                </Tooltip>
                            );
                        })}
                    </Box>
                </Collapse>

                <Button size="small" onClick={handleQuickSave} endIcon={<SaveOutlinedIcon sx={{ color: ortexColor }} />} sx={{ color: textColor, mt: 1, mr: 1 }}>
                    <Typography variant="overline">Save current filters</Typography>
                </Button>

                {/* Overwrite picker — shown only when all slots are full */}
                <Dialog open={overwriteDialogOpen} onClose={() => setOverwriteDialogOpen(false)} maxWidth="xs" fullWidth>
                    <DialogTitle sx={{ pb: 1 }}>Replace a preset</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
                            All {maxSets} slots are in use. Choose one to replace:
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            {filterSets.map((s, i) => (
                                <Button key={i} size="small" variant="outlined" onClick={() => handleOverwrite(i)} sx={{ justifyContent: "flex-start", textTransform: "none" }}>
                                    {s.name}
                                </Button>
                            ))}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOverwriteDialogOpen(false)}>Cancel</Button>
                    </DialogActions>
                </Dialog>

                {/* Rename dialog */}
                <Dialog
                    open={!!renameId}
                    onClose={() => {
                        setRenameId(null);
                        setRenameName("");
                    }}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle sx={{ pb: 1 }}>Rename preset</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            label="Name"
                            fullWidth
                            value={renameName}
                            onChange={(e) => setRenameName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleRename();
                            }}
                            size="small"
                            sx={{ mt: 1 }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => {
                                setRenameId(null);
                                setRenameName("");
                            }}
                        >
                            Cancel
                        </Button>
                        <Button onClick={handleRename} disabled={!renameName.trim()} variant="contained">
                            Save
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
                    <DialogTitle>Delete preset?</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            <b>&quot;{filterSets.find((s) => s.id === deleteId)?.name}&quot;</b> will be permanently deleted.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteId(null)}>Cancel</Button>
                        <Button onClick={confirmDeleteNamedSet} color="error" variant="contained">
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>

                <SnackBar open={snackBar.open} setOpen={(open) => setSnackBar({ ...snackBar, open })} severity={snackBar.severity} text={snackBar.text} />
            </Box>
        );
    }

    // Original single-set UI (unchanged)
    const handleSavingFilters = (actionType: "save" | "delete") => {
        const newSavedDGFilters = { ...user.settings.module_settings?.["datagrid->saved-filters"] };

        if (actionType === "save") {
            newSavedDGFilters[tableId] = {
                customFilterModel: {
                    items: values,
                },
                ...(columnConfiguration ? { columnConfiguration } : newSavedDGFilters?.[tableId]?.columnConfiguration ? { columnConfiguration: newSavedDGFilters?.[tableId]?.columnConfiguration } : {}),
            };
            if (columnConfiguration && onColumnConfigurationSaved) {
                onColumnConfigurationSaved(columnConfiguration);
            }
        } else if (actionType === "delete") {
            delete newSavedDGFilters[tableId];
            onColumnConfigurationDeleted && onColumnConfigurationDeleted();
        }

        persistSettings(newSavedDGFilters, actionType === "save" ? "Filters saved" : "Filters deleted");
        if (actionType === "delete") resetTable();
        setOpen(false);
    };

    return (
        <Box sx={{ width: "100%", mb: is4k ? 3 : 1 }}>
            <Button
                onClick={() => {
                    setOpen(true);
                    setDialogType("save");
                }}
                aria-label="save current filters"
                size="small"
                sx={{
                    color: textColor,
                    mt: 1,
                    mr: 1,
                    "&:disabled": {
                        color: "grey",
                    },
                }}
                endIcon={<SaveOutlinedIcon sx={{ color: ortexColor }} />}
            >
                <Typography variant="overline">Save filters</Typography>
            </Button>
            <Button
                onClick={() => {
                    setOpen(true);
                    setDialogType("delete");
                }}
                aria-label="delete saved filters"
                size="small"
                sx={{
                    color: textColor,
                    mt: 1,
                    mr: 1,
                    "&:disabled": {
                        color: "grey",
                    },
                }}
                endIcon={<DeleteOutlineIcon color={hasSavedFilters ? "error" : "disabled"} />}
                disabled={!hasSavedFilters}
            >
                <Typography variant="overline">Delete saved filters</Typography>
            </Button>

            <Dialog open={Boolean(open)} onClose={() => setOpen(false)} aria-labelledby="confirm-saved-filters-changes" aria-describedby="confirmation-description-saved-filters-changes">
                <DialogTitle id="confirm-saved-filters-changes">{`Are you sure you want to ${dialogType === "save" ? "save the current" : "delete the saved"} filters?`}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="confirmation-description-saved-filters-changes">
                        {dialogType === "save" ? (
                            <>
                                This will <b>override</b> any other filters saved for this table.
                            </>
                        ) : (
                            <>
                                This will <b>delete</b> the saved filters and clear them from the table.
                            </>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} color="error">
                        Cancel
                    </Button>
                    <Button onClick={() => handleSavingFilters(dialogType)}>Accept</Button>
                </DialogActions>
            </Dialog>
            <SnackBar open={snackBar.open} setOpen={(open) => setSnackBar({ ...snackBar, open })} severity={snackBar.severity} text={snackBar.text} />
        </Box>
    );
};

export default SaveFiltersButton;
